import ArrowDropDownIcon from "@mui/icons-material/ArrowDropDown";
import { ReactComponent as CheckIcon } from "icons/redesign/datePicker/check.svg";
import Button from "@mui/material/Button";
import Menu from "@mui/material/Menu";
import MenuItem from "@mui/material/MenuItem";
import { makeStyles } from "components/mui-core";
import { months } from "components-redesign/DatetimeInput/constants";
import { FIGMA_COLORS } from "components-redesign/styles/colors";
import { ReactComponent as NavigateBeforeIconSvg } from "icons/redesign/datePicker/navigate-before.svg";
import { ReactComponent as NavigateNextIconSvg } from "icons/redesign/datePicker/navigate-next.svg";
import React, { FC, useCallback, useMemo, useRef } from "react";

interface Props {
  date: Date;
  changeYear(year: number): void;
  changeMonth(month: number): void;
  customHeaderCount: number;
  decreaseMonth(): void;
  increaseMonth(): void;
  prevMonthButtonDisabled: boolean;
  nextMonthButtonDisabled: boolean;
  decreaseYear(): void;
  increaseYear(): void;
  prevYearButtonDisabled: boolean;
  nextYearButtonDisabled: boolean;
}

const useStyles = makeStyles()({
  headerContainer: {
    height: 64,
    fontSize: 14,
    fontWeight: 500,
    lineHeight: "20px",
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
    padding: 0,
  },
  headerContainerOpen: {
    borderBottom: `1px solid ${FIGMA_COLORS.outlineVariantLight}`,
  },
  navButton: {
    background: "none",
    border: "none",
    cursor: "pointer",
    width: 48,
    height: 48,
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
  },
  navButtonHidden: {
    visibility: "hidden",
  },
  flexCenter: {
    display: "flex",
    alignItems: "center",
  },
  menuItemBase: {
    paddingLeft: "48px !important",
    position: "relative",
    height: "40px",
    "&.Mui-selected, &.Mui-selected:hover": {
      backgroundColor: "rgba(0, 0, 0, 0.08) !important",
    },
  },
  checkIcon: {
    position: "absolute",
    left: "12px",
    top: "50%",
    transform: "translateY(-50%)",
  },
});

const menuPaperStyles = {
  maxHeight: 330,
  boxShadow: "none",
  borderBottomLeftRadius: 16,
  borderBottomRightRadius: 16,
  "&::-webkit-scrollbar": {
    background: "transparent",
    width: "8px",
  },
  "&::-webkit-scrollbar-track": {
    background: "transparent",
  },
  "&::-webkit-scrollbar-thumb": {
    background: `${FIGMA_COLORS.outlineLight}`,
    borderRadius: "10px",
  },
};

const menuTitleStyles = {
  textTransform: "none",
  color: "inherit",
  fontSize: "14px",
  fontWeight: 500,
  minWidth: "unset",
  padding: "4px 8px",
};

const DatePickerHeader: FC<Props> = ({
  date,
  decreaseMonth,
  increaseMonth,
  increaseYear,
  decreaseYear,
  changeMonth,
  changeYear,
}) => {
  const currentMonth = new Date(date).getMonth();
  const monthName = new Date(date).toLocaleString("en-US", { month: "short" });
  const currentYear = new Date(date).getFullYear();
  const headerRef = useRef<HTMLDivElement>(null);

  const [monthAnchorEl, setMonthAnchorEl] = React.useState<null | HTMLElement>(
    null
  );
  const isMonthMenuOpen = Boolean(monthAnchorEl);
  const handleMonthClick = useCallback(() => {
    setMonthAnchorEl(headerRef.current);
  }, []);

  const handleMonthClose = () => {
    setMonthAnchorEl(null);
  };
  const handleMonthSelect = (monthIndex: number) => {
    changeMonth(monthIndex);
    handleMonthClose();
  };

  const [yearAnchorEl, setYearAnchorEl] = React.useState<null | HTMLElement>(
    null
  );
  const isYearMenuOpen = Boolean(yearAnchorEl);
  const handleYearClick = useCallback(() => {
    setYearAnchorEl(headerRef.current);
  }, []);

  const handleYearClose = () => {
    setYearAnchorEl(null);
  };
  const handleYearSelect = (yearValue: number) => {
    changeYear(yearValue);
    handleYearClose();
  };

  const minYear = new Date().getFullYear() - 10;
  const maxFutureYear = minYear + 20;

  const years = useMemo(
    () =>
      Array.from(
        { length: maxFutureYear - minYear + 1 },
        (_, i) => minYear + i
      ),
    [minYear, maxFutureYear]
  );

  const isMenuOpen = useMemo(
    () => isYearMenuOpen || isMonthMenuOpen,
    [isMonthMenuOpen, isYearMenuOpen]
  );

  const { classes, cx } = useStyles();

  return (
    <div
      ref={headerRef}
      className={cx(classes.headerContainer, {
        [classes.headerContainerOpen]: isMenuOpen,
      })}>
      <div className={classes.flexCenter}>
        <button
          onClick={decreaseMonth}
          className={cx(classes.navButton, {
            [classes.navButtonHidden]: isMenuOpen,
          })}>
          <NavigateBeforeIconSvg />
        </button>
        <Button
          onClick={handleMonthClick}
          aria-expanded={isMonthMenuOpen ? "true" : undefined}
          disabled={isYearMenuOpen}
          endIcon={
            <ArrowDropDownIcon
              sx={{ ...(isYearMenuOpen ? { visibility: "hidden" } : {}) }}
            />
          }
          sx={menuTitleStyles}>
          {monthName}
        </Button>
        <Menu
          id="month-picker-menu"
          onClose={handleMonthClose}
          anchorEl={headerRef.current}
          open={isMonthMenuOpen}
          style={{
            zIndex: 100000,
          }}
          PaperProps={{
            sx: {
              width: headerRef.current?.offsetWidth || 345,
              ...menuPaperStyles,
            },
          }}>
          {months.map((month, index) => (
            <MenuItem
              key={month}
              onClick={() => handleMonthSelect(index)}
              selected={index === currentMonth}
              className={classes.menuItemBase}>
              {index === currentMonth && (
                <CheckIcon
                  fontSize="small"
                  className={classes.checkIcon}
                />
              )}
              {month}
            </MenuItem>
          ))}
        </Menu>
        <button
          onClick={increaseMonth}
          className={cx(classes.navButton, {
            [classes.navButtonHidden]: isMenuOpen,
          })}>
          <NavigateNextIconSvg />
        </button>
      </div>
      <div className={classes.flexCenter}>
        <button
          onClick={decreaseYear}
          className={cx(classes.navButton, {
            [classes.navButtonHidden]: isMenuOpen,
          })}>
          <NavigateBeforeIconSvg />
        </button>
        <Button
          onClick={handleYearClick}
          aria-expanded={isYearMenuOpen ? "true" : undefined}
          disabled={isMonthMenuOpen}
          endIcon={
            <ArrowDropDownIcon
              sx={{ ...(isMonthMenuOpen ? { visibility: "hidden" } : {}) }}
            />
          }
          sx={menuTitleStyles}>
          {currentYear}
        </Button>
        <Menu
          id="year-picker-menu"
          onClose={handleYearClose}
          anchorEl={headerRef.current}
          open={isYearMenuOpen}
          style={{
            zIndex: 100000,
          }}
          PaperProps={{
            sx: {
              width: headerRef.current?.offsetWidth || 345,
              ...menuPaperStyles,
            },
          }}>
          {years.map((yearOption) => (
            <MenuItem
              key={yearOption}
              onClick={() => handleYearSelect(yearOption)}
              selected={yearOption === currentYear}
              className={classes.menuItemBase}>
              {yearOption === currentYear && (
                <CheckIcon
                  fontSize="small"
                  className={classes.checkIcon}
                />
              )}
              {yearOption}
            </MenuItem>
          ))}
        </Menu>
        <button
          onClick={increaseYear}
          className={cx(classes.navButton, {
            [classes.navButtonHidden]: isMenuOpen,
          })}>
          <NavigateNextIconSvg />
        </button>
      </div>
    </div>
  );
};

export default (params: Props) => <DatePickerHeader {...params} />;
